import { AfterViewInit, Component, Input, OnDestroy, output, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';

import { TBotItem, UiConfiguration } from '@core/interfaces/TBotItem';

import { SpinnerComponent } from '../../layout/components/atoms/spinner/spinner.component';
import { Conversation } from '../infrastructure/models/Conversation';
import { AgentConversationService } from '../services/agent-conversation.service';
import { ConversationDataService } from '../services/conversation-data.service';
import { HistoryConversationService } from '../services/history-conversation.service';
import { IInputTransferData } from '../types/dtos';
import { UIMessageReferenceProgress } from '../types/types';
import { FeatureService } from '@shared/services/feature.service';
import { MultiSummaryService } from '@shared/services/multi-summary.service';

import { AutoScrollDirective } from '../infrastructure/directives/auto-scroll.directive';
import { ConversationMessageComponent } from './conversation-message/conversation-message.component';
import { ConversationInputComponent } from './partials/conversation-input/conversation-input.component';
import { ConversationInputDynamicComponent } from './partials/conversation-input-dynamic/conversation-input-dynamic.component';
import { StatusCenterComponent } from './partials/status-center/status-center.component';
import { WelcomeMessageComponent } from './partials/welcome-message/welcome-message.component';

@Component({
    selector: 'app-agent-conversation',
    templateUrl: './agent-conversation.component.html',
    styleUrls: ['./agent-conversation.component.scss'],
    host: {
      '[class.agent-conversation--landing]': 'conversationInputExtended',
    },
    standalone: true,
    imports: [
      AutoScrollDirective,
      SpinnerComponent,
      WelcomeMessageComponent,
      ConversationMessageComponent,
      StatusCenterComponent,
      ConversationInputComponent,
      ConversationInputDynamicComponent
    ]
})
export class AgentConversationComponent implements OnDestroy, AfterViewInit {
  @ViewChild('messagesContainer') autoScrollDir!: AutoScrollDirective;
  @Input() conversationInputExtended = false;
  @Input() filtersButtonEnabled = true;
  @Input() shouldShowWelcome = true;
  @Input() hideWelcomeOnChatFeature = false;
  @Input() uiConfiguration?: UiConfiguration;
  @Input() initialMessage = '';
  @Input() inputDisabledReason: string | null = null;
  protected allowAttachFile = true;
  protected agent: TBotItem | null = null;
  protected conversation: Conversation | null = null;
  protected isLoading = false;
  protected isWriting = false;
  protected isMultiSummaryVisible = false;

  private _subs: Subscription[] = [];

  constructor(
    private _historyConversationService: HistoryConversationService,
    private _conversationData: ConversationDataService,
    protected agentConversationService: AgentConversationService,
    private _multiSummaryService: MultiSummaryService,
    private _featureService: FeatureService
  ) {}

  @Input() set talkingWith(agent: TBotItem | null | undefined) {
    const normalized = agent ?? null;
    const agentChanged = this.agent?._id !== normalized?._id;
    this.agent = normalized;
    if (normalized && agentChanged && this.conversation?.type !== 'results') {
      this.agentConversationService.newConversation(normalized);
    }
  }

  /** Emits while history is being fetched (results / deep-linked conversations). */
  readonly historyLoadingChange = output<boolean>();

  @Input() set conversationId(conversationId: string) {
    if (!conversationId) {
      return;
    }
    this.loadHistoryConversationById(conversationId);
  }

  ngAfterViewInit(): void {
    this._conversationData.emitComponentReady();
    this._conversationData.isWriting$.subscribe(isWriting => {
      this.isWriting = isWriting;
    });
    this._subs.push(
      this._conversationData.conversation$.subscribe(conv => {
        this.conversation = conv;
        // Disabled auto-scroll - user has full control
        // if (this.autoScrollDir.autoScroll) {
        //   setTimeout(() => this.autoScrollDir.scrollToBottom());
        // }
      }),
      this._multiSummaryService.getVisibilityState().subscribe(isVisible => {
        this.isMultiSummaryVisible = isVisible;
      })
    );
  }

  ngOnDestroy(): void {
    this._historyConversationService.cancelActiveRequest();
    this._subs.forEach(sub => sub.unsubscribe());
  }

  get conversationInputOptions() {
    return {
      allowAttachFile: this.allowAttachFile,
      isExtended: this.conversationInputExtended,
      promptHints: this.agent?.promptHints ?? [],
      filterButton: this.filtersButtonEnabled,
    };
  }
  protected onMessageSubmit(message: IInputTransferData) {
    this.agentConversationService.askQuestionToAgent(message);
  }

  protected loadHistoryConversationById(conversationId: string) {
    this.allowAttachFile = false;
    this.isLoading = true;
    this.historyLoadingChange.emit(true);
    this._historyConversationService.getConversationById(
      conversationId,
      () => {
        this.isLoading = false;
        this.historyLoadingChange.emit(false);
      },
      () => {
        this.isLoading = false;
        this.historyLoadingChange.emit(false);
      }
    );
  }

  protected get shouldShowWelcomeMessage(): boolean {
    // Hide welcome message if hideWelcomeOnChatFeature is true AND 'bot-ui-config' feature is enabled
    if (this.hideWelcomeOnChatFeature && this._featureService.isEnabled('bot-ui-config')) {
      return false;
    }
    return true;
  }

  /** Reference generation progress for the last assistant message; used to show status above the input. */
  protected get referenceProgressAboveInput(): UIMessageReferenceProgress | undefined {
    const last = this.conversation?.getLastMessageByAuthor('assistant');
    return last?.uiReferenceStatus;
  }
}
